// Shared message types for WebSocket communication
// This mirrors worker/protocol.ts for client-side use

import { z } from 'zod';

export type WizardType = 'pyromancer' | 'frostmage' | 'battlemage';
export type SpellType = 'fireball' | 'iceShard' | 'shield' | 'lightningBolt' | 'healSpell' | 'reflect' | 'teleport' | 'meteor' | 'superShield' | 'blizzard';
export type CheerType = 'applause' | 'wow' | 'laugh' | 'boo';
export type EmoteType = 'well_played' | 'nice_combo' | 'gg' | 'wow_emote';
export type PlayerRole = 'player1' | 'player2' | 'spectator';
export type RankTier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'master';
export type ChallengeMode = 'mirror_match' | 'rapid_fire' | 'one_spell_only' | 'glass_cannon';

// Zod schemas for validation
const rankTierSchema = z.enum(['bronze', 'silver', 'gold', 'platinum', 'diamond', 'master']);
const challengeModeSchema = z.enum(['mirror_match', 'rapid_fire', 'one_spell_only', 'glass_cannon']);
const wizardTypeSchema = z.enum(['pyromancer', 'frostmage', 'battlemage']);
const spellTypeSchema = z.enum(['fireball', 'iceShard', 'shield', 'lightningBolt', 'healSpell', 'reflect', 'teleport', 'meteor', 'superShield', 'blizzard']);
const cheerTypeSchema = z.enum(['applause', 'wow', 'laugh', 'boo']);
const emoteTypeSchema = z.enum(['well_played', 'nice_combo', 'gg', 'wow_emote']);
const playerRoleSchema = z.enum(['player1', 'player2', 'spectator']);

const playerStateSchema = z.object({
  name: z.string().optional(),
  wizardType: wizardTypeSchema.optional(),
  ready: z.boolean(),
  health: z.number(),
  connected: z.boolean(),
});

const gameStateSchema = z.object({
  player1: playerStateSchema,
  player2: playerStateSchema,
  started: z.boolean(),
  winner: playerRoleSchema.optional(),
});

const roomStateSchema = z.object({
  roomId: z.string(),
  player1: playerStateSchema.optional(),
  player2: playerStateSchema.optional(),
  spectatorCount: z.number(),
  gameStarted: z.boolean(),
});

// Player stats schema
const playerStatsSchema = z.object({
  playerId: z.string(),
  name: z.string(),
  elo: z.number(),
  wins: z.number(),
  losses: z.number(),
  draws: z.number(),
  totalMatches: z.number(),
  dust: z.number().optional().default(0),
});

// Dust/betting schemas
const dustTransactionTypeSchema = z.enum(['match_win', 'match_loss', 'achievement', 'tournament_win', 'daily_login', 'bet_win', 'bet_loss', 'shop_purchase', 'bet_refund']);

const betPoolStateSchema = z.object({
  player1Total: z.number(),
  player2Total: z.number(),
  yourBet: z.object({ amount: z.number(), backedPlayer: playerRoleSchema }).optional(),
  locked: z.boolean(),
});

const betResultSchema = z.object({
  won: z.boolean(),
  payout: z.number(),
  profit: z.number(),
  newBalance: z.number(),
});

const shopItemSchema = z.object({
  cosmeticId: z.string(),
  name: z.string(),
  type: z.string(),
  description: z.string(),
  price: z.number(),
});

// Match result schema
const matchResultSchema = z.object({
  matchId: z.string(),
  roomId: z.string(),
  player1Name: z.string(),
  player1Wizard: wizardTypeSchema,
  player2Name: z.string(),
  player2Wizard: wizardTypeSchema,
  winner: z.union([playerRoleSchema, z.literal('draw')]),
  player1EloChange: z.number(),
  player2EloChange: z.number(),
  duration: z.number(),
  timestamp: z.number(),
});

// Season info schema
const seasonInfoSchema = z.object({
  seasonNumber: z.number(),
  startDate: z.number(),
  endDate: z.number(),
  daysRemaining: z.number(),
  isActive: z.boolean(),
});

// Seasonal player stats schema
const seasonalPlayerStatsSchema = z.object({
  playerId: z.string(),
  name: z.string(),
  seasonNumber: z.number(),
  seasonElo: z.number(),
  peakElo: z.number(),
  seasonWins: z.number(),
  seasonLosses: z.number(),
  rankTier: rankTierSchema,
  lastPlayedAt: z.number(),
});

// Challenge mode config schema
const challengeModeConfigSchema = z.object({
  mode: challengeModeSchema,
  name: z.string(),
  description: z.string(),
  healthOverride: z.number().optional(),
  damageMultiplier: z.number().optional(),
  cooldownMultiplier: z.number().optional(),
  forcedSpell: spellTypeSchema.optional(),
  forcedWizard: wizardTypeSchema.optional(),
});

// Server message schemas
export const serverMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('joined'), role: playerRoleSchema, roomState: roomStateSchema }),
  z.object({ type: z.literal('player_joined'), role: playerRoleSchema, name: z.string().optional(), roomState: roomStateSchema }),
  z.object({ type: z.literal('wizard_selected'), player: playerRoleSchema, wizardType: wizardTypeSchema }),
  z.object({ type: z.literal('player_ready'), player: playerRoleSchema }),
  z.object({ type: z.literal('game_start'), gameState: gameStateSchema }),
  z.object({ type: z.literal('spell_cast'), caster: playerRoleSchema, spell: spellTypeSchema, timestamp: z.number() }),
  z.object({ type: z.literal('damage'), target: playerRoleSchema, amount: z.number(), newHealth: z.number(), shieldAbsorbed: z.number() }),
  z.object({ type: z.literal('slow_applied'), target: playerRoleSchema, duration: z.number(), multiplier: z.number() }),
  z.object({ type: z.literal('position_update'), player: playerRoleSchema, x: z.number(), z: z.number() }),
  z.object({ type: z.literal('game_over'), winner: playerRoleSchema, matchResult: matchResultSchema.optional() }),
  z.object({ type: z.literal('cheer'), from: z.string(), cheerType: cheerTypeSchema, timestamp: z.number() }),
  z.object({ type: z.literal('spectator_count'), count: z.number() }),
  z.object({ type: z.literal('player_disconnected'), player: playerRoleSchema }),
  z.object({ type: z.literal('error'), message: z.string() }),
  z.object({ type: z.literal('player_stats'), stats: playerStatsSchema }),
  z.object({ type: z.literal('match_stats'), player1Spells: z.number(), player2Spells: z.number(), duration: z.number() }),
  z.object({ type: z.literal('spectator_chat'), from: z.string(), message: z.string(), timestamp: z.number() }),
  z.object({ type: z.literal('rate_limited'), cooldownRemaining: z.number() }),
  z.object({ type: z.literal('emote'), from: z.string(), emoteType: emoteTypeSchema, player: playerRoleSchema, timestamp: z.number() }),
  z.object({ type: z.literal('season_info'), season: seasonInfoSchema, playerSeasonStats: seasonalPlayerStatsSchema.optional() }),
  z.object({ type: z.literal('challenge_mode_set'), mode: challengeModeSchema.nullable(), config: challengeModeConfigSchema.optional() }),
  z.object({ type: z.literal('dust_balance'), dust: z.number() }),
  z.object({ type: z.literal('bet_placed'), pool: betPoolStateSchema }),
  z.object({ type: z.literal('bet_locked'), pool: betPoolStateSchema }),
  z.object({ type: z.literal('bet_result'), result: betResultSchema }),
  z.object({ type: z.literal('dust_earned'), amount: z.number(), reason: dustTransactionTypeSchema, newBalance: z.number() }),
  z.object({ type: z.literal('purchase_result'), success: z.boolean(), cosmeticId: z.string(), newBalance: z.number(), error: z.string().optional() }),
  z.object({ type: z.literal('shop_items'), items: z.array(shopItemSchema) }),
]);

export function validateServerMessage(data: unknown): ServerMessage | null {
  const result = serverMessageSchema.safeParse(data);
  if (result.success) {
    return result.data as ServerMessage;
  }
  console.warn('Invalid server message:', result.error.format());
  return null;
}

// Dust transaction types
export type DustTransactionType = 'match_win' | 'match_loss' | 'achievement' | 'tournament_win' | 'daily_login' | 'bet_win' | 'bet_loss' | 'shop_purchase' | 'bet_refund';

// Bet pool state
export interface BetPoolState {
  player1Total: number;
  player2Total: number;
  yourBet?: { amount: number; backedPlayer: PlayerRole };
  locked: boolean;
}

// Bet result
export interface BetResult {
  won: boolean;
  payout: number;
  profit: number;
  newBalance: number;
}

// Shop item
export interface ShopItem {
  cosmeticId: string;
  name: string;
  type: string;
  description: string;
  price: number;
}

// Player stats stored persistently
export interface PlayerStats {
  playerId: string;
  name: string;
  elo: number;
  wins: number;
  losses: number;
  draws: number;
  totalMatches: number;
  dust: number;
}

// Match result stored persistently
export interface MatchResult {
  matchId: string;
  roomId: string;
  player1Name: string;
  player1Wizard: WizardType;
  player2Name: string;
  player2Wizard: WizardType;
  winner: PlayerRole | 'draw';
  player1EloChange: number;
  player2EloChange: number;
  duration: number;
  timestamp: number;
}

// Leaderboard entry for API responses
export interface LeaderboardEntry {
  rank: number;
  name: string;
  elo: number;
  wins: number;
  losses: number;
  draws: number;
}

// Season info
export interface SeasonInfo {
  seasonNumber: number;
  startDate: number;
  endDate: number;
  daysRemaining: number;
  isActive: boolean;
}

// Seasonal player stats
export interface SeasonalPlayerStats {
  playerId: string;
  name: string;
  seasonNumber: number;
  seasonElo: number;
  peakElo: number;
  seasonWins: number;
  seasonLosses: number;
  rankTier: RankTier;
  lastPlayedAt: number;
}

// Challenge mode config
export interface ChallengeModeConfig {
  mode: ChallengeMode;
  name: string;
  description: string;
  healthOverride?: number;
  damageMultiplier?: number;
  cooldownMultiplier?: number;
  forcedSpell?: SpellType;
  forcedWizard?: WizardType;
}

// Client -> Server messages
export type ClientMessage =
  | { type: 'join'; name?: string; playerId?: string }
  | { type: 'wizard_select'; wizardType: WizardType }
  | { type: 'ready' }
  | { type: 'cast_spell'; spell: SpellType }
  | { type: 'position_update'; x: number; z: number }
  | { type: 'cheer'; cheerType: CheerType }
  | { type: 'request_stats' }
  | { type: 'spectator_chat'; message: string }
  | { type: 'emote'; emoteType: EmoteType }
  | { type: 'request_season_info' }
  | { type: 'set_challenge_mode'; mode: ChallengeMode | null }
  | { type: 'place_bet'; amount: number; backedPlayer: PlayerRole }
  | { type: 'request_dust_balance' }
  | { type: 'purchase_cosmetic'; cosmeticId: string };

// Server -> Client messages
export type ServerMessage =
  | { type: 'joined'; role: PlayerRole; roomState: RoomState }
  | { type: 'player_joined'; role: PlayerRole; name?: string; roomState: RoomState }
  | { type: 'wizard_selected'; player: PlayerRole; wizardType: WizardType }
  | { type: 'player_ready'; player: PlayerRole }
  | { type: 'game_start'; gameState: GameState }
  | { type: 'spell_cast'; caster: PlayerRole; spell: SpellType; timestamp: number }
  | { type: 'damage'; target: PlayerRole; amount: number; newHealth: number; shieldAbsorbed: number }
  | { type: 'slow_applied'; target: PlayerRole; duration: number; multiplier: number }
  | { type: 'position_update'; player: PlayerRole; x: number; z: number }
  | { type: 'game_over'; winner: PlayerRole; matchResult?: MatchResult }
  | { type: 'cheer'; from: string; cheerType: CheerType; timestamp: number }
  | { type: 'spectator_count'; count: number }
  | { type: 'player_disconnected'; player: PlayerRole }
  | { type: 'error'; message: string }
  | { type: 'player_stats'; stats: PlayerStats }
  | { type: 'match_stats'; player1Spells: number; player2Spells: number; duration: number }
  | { type: 'spectator_chat'; from: string; message: string; timestamp: number }
  | { type: 'rate_limited'; cooldownRemaining: number }
  | { type: 'emote'; from: string; emoteType: EmoteType; player: PlayerRole; timestamp: number }
  | { type: 'season_info'; season: SeasonInfo; playerSeasonStats?: SeasonalPlayerStats }
  | { type: 'challenge_mode_set'; mode: ChallengeMode | null; config?: ChallengeModeConfig }
  | { type: 'dust_balance'; dust: number }
  | { type: 'bet_placed'; pool: BetPoolState }
  | { type: 'bet_locked'; pool: BetPoolState }
  | { type: 'bet_result'; result: BetResult }
  | { type: 'dust_earned'; amount: number; reason: DustTransactionType; newBalance: number }
  | { type: 'purchase_result'; success: boolean; cosmeticId: string; newBalance: number; error?: string }
  | { type: 'shop_items'; items: ShopItem[] };

export interface PlayerState {
  name?: string;
  wizardType?: WizardType;
  ready: boolean;
  health: number;
  connected: boolean;
}

export interface GameState {
  player1: PlayerState;
  player2: PlayerState;
  started: boolean;
  winner?: PlayerRole;
}

export interface RoomState {
  roomId: string;
  player1?: PlayerState;
  player2?: PlayerState;
  spectatorCount: number;
  gameStarted: boolean;
}
